<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { StatusOption } from "@/entities/evaluation/api";

import { useDeviceStore } from "@/entities/device/model";
import {
    useApiGetStatusQuestion,
    useApiSendStatusAnswer,
    useEvaluationStore,
} from "@/entities/evaluation/model";
import { HandedDevice } from "@/entities/evaluation/ui";
import { logger } from "@/shared/lib";
import { useGlobalStore } from "@/shared/model";
import { UiNotification, UiSkeleton, UiStepLayout, startViewTransition } from "@/shared/ui";

import StatusOptions from "./StatusOptions.vue";

/** Скелетон повторяет варианты состояния, пока грузится вопрос */
const OPTIONS_SKELETON_COUNT = 3;

const { t } = useI18n({ useScope: "global" });
const globalStore = useGlobalStore();
const deviceStore = useDeviceStore();
const evaluationStore = useEvaluationStore();

const {
    data: question,
    isPending: isQuestionPending,
    error: questionError,
} = useApiGetStatusQuestion();
const {
    mutateAsync: sendAnswer,
    isLoading: isAnswerSending,
    variables: answerVariables,
    error: answerError,
} = useApiSendStatusAnswer();

watch(questionError, (value) => value && logger.error("status question is not loaded", value));

const title = computed(() => question.value?.question ?? "");

const loadingOptionId = computed(() =>
    isAnswerSending.value ? (answerVariables.value?.optionId ?? null) : null,
);

async function selectOption(option: StatusOption): Promise<void> {
    if (!question.value) return;

    try {
        await sendAnswer({ questionId: question.value.questionId, optionId: option.cid });
    } catch (error) {
        logger.error("status answer is not sent", error);
        return;
    }

    await globalStore.switchStep("leaveContacts");
}

// Stores are reset inside the transition, so the old snapshot keeps the filled screen
function editDevice(): void {
    void startViewTransition(() => {
        evaluationStore.reset();
        globalStore.resetStep();
    });
}
</script>

<template>
    <UiStepLayout>
        <template #header>
            <UiSkeleton v-if="!title && isQuestionPending" width="300px" height="20px" />
            <h2 v-else-if="title" class="title">{{ title }}</h2>
            <p v-if="question?.description" class="subtitle">
                {{ question.description }}
            </p>
        </template>

        <HandedDevice
            :chain="deviceStore.chain"
            :edit-disabled="isAnswerSending"
            @edit="editDevice"
        />

        <!-- Выбор состояния устройства -->
        <UiNotification v-if="questionError" type="danger">
            {{ t("common.error") }}
        </UiNotification>

        <div v-else-if="isQuestionPending" class="skeleton">
            <UiSkeleton v-for="item in OPTIONS_SKELETON_COUNT" :key="item" height="100px" />
        </div>

        <template v-else-if="question">
            <StatusOptions
                :options="question.options"
                :selected-id="evaluationStore.answeredOptionId"
                :loading-id="loadingOptionId"
                @select="selectOption"
            />

            <UiNotification v-if="answerError" type="danger" class="answer-error">
                {{ t("common.error") }}
            </UiNotification>
        </template>
    </UiStepLayout>
</template>

<style scoped>
.title {
    margin: 0;
    font-size: calc(var(--custom-font-size) * 1.429);
    font-weight: 700;
    line-height: 1.2;
}

.subtitle {
    margin: 8px 0 0;
    font-size: var(--custom-font-size);
    line-height: 1.429;
}

.skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.answer-error {
    margin-top: 16px;
}
</style>
